import { useState, useMemo } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { DollarSign, Home, TrendingUp, Calendar } from 'lucide-react';

const FinancialPlanner = () => {
  // State for all adjustable variables
  const [purchaseDate, setPurchaseDate] = useState('2029-06');
  const [monthlySavings, setMonthlySavings] = useState(1000);
  const [monthlyEmergency, setMonthlyEmergency] = useState(800);
  const [monthlyFHSS, setMonthlyFHSS] = useState(1450);
  const [currentSavings] = useState(110000);
  const [currentEmergency] = useState(1000);
  const [mortgageRate, setMortgageRate] = useState(6.5);
  const [savingsRate] = useState(4.25);
  const [propertyCost, setPropertyCost] = useState(575000);
  const [loanTerm, setLoanTerm] = useState(10);
  const [payoffStrategy, setPayoffStrategy] = useState('balanced');
  const [superGrowthRate, setSuperGrowthRate] = useState(7);
  const [propertyGrowthRate, setPropertyGrowthRate] = useState(2);
  
  // Calculate everything
  const calculations = useMemo(() => {
    const startYear = 2025;
    const startMonth = 10;
    const [purchaseYear, purchaseMonth] = purchaseDate.split('-').map(Number);
    const monthsToPurchase = (purchaseYear - startYear) * 12 + (purchaseMonth - startMonth);
    
    // Calculate savings at purchase
    const savingsAtPurchase = currentSavings * Math.pow(1 + savingsRate/100/12, monthsToPurchase) + 
      monthlySavings * ((Math.pow(1 + savingsRate/100/12, monthsToPurchase) - 1) / (savingsRate/100/12));
    
    const emergencyAtPurchase = currentEmergency * Math.pow(1 + 0.02/12, monthsToPurchase) + 
      monthlyEmergency * ((Math.pow(1 + 0.02/12, monthsToPurchase) - 1) / (0.02/12));
    
    // FHSS calculation (simplified)
    const monthsOfFHSS = Math.min(monthsToPurchase, 44);
    const fhssContributions = Math.min(monthlyFHSS * monthsOfFHSS, 50000);
    const fhssWithEarnings = fhssContributions * 1.14; // simplified deemed earnings
    const fhssNet = fhssWithEarnings * 0.85; // after 15% tax
    
    const totalDeposit = savingsAtPurchase + emergencyAtPurchase + fhssNet;
    const loanAmount = propertyCost - totalDeposit;
    const depositPercent = (totalDeposit / propertyCost) * 100;
    
    // Monthly mortgage payment
    const monthlyRate = mortgageRate / 100 / 12;
    const numPayments = loanTerm * 12;
    const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
      (Math.pow(1 + monthlyRate, numPayments) - 1);
    
    // Freed cash flow
    const currentOutgoings = 4800 + monthlyFHSS + monthlySavings + monthlyEmergency;
    const freedCash = currentOutgoings - monthlyPayment;
    
    // Strategy splits
    let extraToMortgage, extraToSuper;
    if (payoffStrategy === 'aggressive') {
      extraToMortgage = freedCash;
      extraToSuper = 0;
    } else if (payoffStrategy === 'balanced') {
      extraToMortgage = freedCash * 0.5;
      extraToSuper = freedCash * 0.5;
    } else {
      extraToMortgage = 0;
      extraToSuper = freedCash;
    }
    
    // Calculate timeline data
    const timelineData = [];
    let currentSuper = 368000;
    let currentLoan = loanAmount;
    let currentProperty = propertyCost;
    let currentEmergencyFund = emergencyAtPurchase;
    const employerSG = 23400;
    
    // Pre-purchase years
    for (let year = 2025; year < purchaseYear; year++) {
      currentSuper = (currentSuper + employerSG) * (1 + superGrowthRate/100);
      timelineData.push({
        year,
        age: year - 1975,
        super: Math.round(currentSuper),
        property: 0,
        loan: 0,
        emergency: year < purchaseYear - 1 ? Math.round(currentEmergency * Math.pow(1 + 0.02, year - 2025)) : Math.round(emergencyAtPurchase),
        netWorth: Math.round(currentSuper + (year < purchaseYear - 1 ? currentEmergency * Math.pow(1 + 0.02, year - 2025) : emergencyAtPurchase))
      });
    }
    
    // Purchase year - FHSS withdrawal
    currentSuper = (currentSuper + employerSG) * (1 + superGrowthRate/100) - fhssNet;
    
    // Post-purchase years
    let mortgageFreeYear = null;
    for (let year = purchaseYear; year <= 2040; year++) {
      // Property growth
      currentProperty *= (1 + propertyGrowthRate/100);
      
      // Mortgage paydown
      if (currentLoan > 0) {
        const totalAnnualPayment = (monthlyPayment + extraToMortgage) * 12;
        const annualInterest = currentLoan * (mortgageRate/100);
        const principalPaid = totalAnnualPayment - annualInterest;
        currentLoan = Math.max(0, currentLoan - principalPaid);
        
        if (currentLoan === 0 && !mortgageFreeYear) {
          mortgageFreeYear = year;
        }
      }
      
      // Super growth
      const annualSuperContrib = employerSG + (extraToSuper * 12);
      currentSuper = (currentSuper + annualSuperContrib) * (1 + superGrowthRate/100);
      
      // Emergency fund stays relatively stable
      currentEmergencyFund = Math.min(50000, emergencyAtPurchase * Math.pow(1.02, year - purchaseYear));
      
      timelineData.push({
        year,
        age: year - 1975,
        super: Math.round(currentSuper),
        property: Math.round(currentProperty),
        loan: Math.round(currentLoan),
        emergency: Math.round(currentEmergencyFund),
        netWorth: Math.round(currentSuper + currentProperty - currentLoan + currentEmergencyFund)
      });
    }
    
    return {
      savingsAtPurchase,
      emergencyAtPurchase,
      fhssNet,
      totalDeposit,
      loanAmount,
      depositPercent,
      monthlyPayment,
      freedCash,
      extraToMortgage,
      extraToSuper,
      timelineData,
      mortgageFreeYear,
      purchaseAge: purchaseYear - 1975,
      netWorthAt60: timelineData.find(d => d.age === 60)?.netWorth || 0,
      superAt60: timelineData.find(d => d.age === 60)?.super || 0
    };
  }, [purchaseDate, monthlySavings, monthlyEmergency, monthlyFHSS, currentSavings, 
      currentEmergency, mortgageRate, savingsRate, propertyCost, loanTerm, 
      payoffStrategy, superGrowthRate, propertyGrowthRate]);

  const MetricCard = ({ icon: Icon, label, value, subtitle, color }: { icon: React.ComponentType<{ className?: string }>, label: string, value: string, subtitle?: string, color: string }) => (
    <div className="bg-white rounded-lg shadow-md p-4 border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
            <Icon className="w-4 h-4" />
            <span>{label}</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          {subtitle && <div className="text-sm text-gray-500 mt-1">{subtitle}</div>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">🏡 Interactive Home & Retirement Planner</h1>
          <p className="text-gray-600">Adjust the variables below to see how they impact your financial future</p>
        </div>

        {/* Key Metrics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard
            icon={Home}
            label="Total Deposit"
            value={`$${Math.round(calculations.totalDeposit).toLocaleString()}`}
            subtitle={`${calculations.depositPercent.toFixed(1)}% of property`}
            color="#3B82F6"
          />
          <MetricCard
            icon={DollarSign}
            label="Monthly Payment"
            value={`$${Math.round(calculations.monthlyPayment).toLocaleString()}`}
            subtitle={`Freed: $${Math.round(calculations.freedCash).toLocaleString()}/mo`}
            color="#10B981"
          />
          <MetricCard
            icon={Calendar}
            label="Mortgage-Free"
            value={String(calculations.mortgageFreeYear || 'N/A')}
            subtitle={`Age ${calculations.mortgageFreeYear ? calculations.mortgageFreeYear - 1975 : 'N/A'}`}
            color="#F59E0B"
          />
          <MetricCard
            icon={TrendingUp}
            label="Net Worth at 60"
            value={`$${(calculations.netWorthAt60 / 1000000).toFixed(2)}M`}
            subtitle={`Super: $${(calculations.superAt60 / 1000000).toFixed(2)}M`}
            color="#8B5CF6"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Control Panel */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-900">📊 Adjust Variables</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                <input
                  type="month"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  min="2025-10"
                  max="2032-12"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly House Savings: ${monthlySavings}
                </label>
                <input
                  type="range"
                  min="500"
                  max="3000"
                  step="100"
                  value={monthlySavings}
                  onChange={(e) => setMonthlySavings(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Emergency Fund: ${monthlyEmergency}
                </label>
                <input
                  type="range"
                  min="0"
                  max="2000"
                  step="100"
                  value={monthlyEmergency}
                  onChange={(e) => setMonthlyEmergency(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly FHSS: ${monthlyFHSS}
                </label>
                <input
                  type="range"
                  min="500"
                  max="2500"
                  step="50"
                  value={monthlyFHSS}
                  onChange={(e) => setMonthlyFHSS(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property Cost: ${propertyCost.toLocaleString()}
                </label>
                <input
                  type="range"
                  min="450000"
                  max="750000"
                  step="5000"
                  value={propertyCost}
                  onChange={(e) => setPropertyCost(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mortgage Rate: {mortgageRate}%
                </label>
                <input
                  type="range"
                  min="4"
                  max="9"
                  step="0.1"
                  value={mortgageRate}
                  onChange={(e) => setMortgageRate(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Loan Term: {loanTerm} years
                </label>
                <input
                  type="range"
                  min="5"
                  max="15"
                  step="1"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Super Growth Rate: {superGrowthRate}%
                </label>
                <input
                  type="range"
                  min="4"
                  max="10"
                  step="0.5"
                  value={superGrowthRate}
                  onChange={(e) => setSuperGrowthRate(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property Growth: {propertyGrowthRate}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="6"
                  step="0.5"
                  value={propertyGrowthRate}
                  onChange={(e) => setPropertyGrowthRate(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payoff Strategy</label>
                <div className="space-y-2">
                  {['aggressive', 'balanced', 'super'].map((strategy) => (
                    <label key={strategy} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="strategy"
                        value={strategy}
                        checked={payoffStrategy === strategy}
                        onChange={(e) => setPayoffStrategy(e.target.value)}
                        className="text-blue-600"
                      />
                      <span className="text-sm capitalize">{strategy === 'super' ? 'Super Priority' : strategy}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t">
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Extra to Mortgage:</strong> ${Math.round(calculations.extraToMortgage).toLocaleString()}/mo</p>
                  <p><strong>Extra to Super:</strong> ${Math.round(calculations.extraToSuper).toLocaleString()}/mo</p>
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="lg:col-span-2 space-y-6">
            {/* Net Worth Over Time */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-bold mb-4 text-gray-900">Net Worth Projection</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={calculations.timelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="age" label={{ value: 'Age', position: 'insideBottom', offset: -5 }} />
                  <YAxis 
                    tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                    label={{ value: 'Net Worth', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value) => `$${value.toLocaleString()}`}
                    labelFormatter={(age) => `Age ${age}`}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="super" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" name="Super" />
                  <Area type="monotone" dataKey="property" stackId="1" stroke="#3B82F6" fill="#3B82F6" name="Property" />
                  <Area type="monotone" dataKey="emergency" stackId="1" stroke="#10B981" fill="#10B981" name="Emergency" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Mortgage vs Super */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-bold mb-4 text-gray-900">Mortgage Paydown vs Super Growth</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={calculations.timelineData.filter(d => d.year >= parseInt(purchaseDate.split('-')[0]))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis 
                    yAxisId="left"
                    tickFormatter={(value) => `$${(value / 1000).toLocaleString()}k`}
                    label={{ value: 'Amount', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="loan" stroke="#EF4444" strokeWidth={2} name="Loan Balance" />
                  <Line yAxisId="left" type="monotone" dataKey="super" stroke="#8B5CF6" strokeWidth={2} name="Super Balance" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Wealth Breakdown at Age 60 */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-bold mb-4 text-gray-900">Wealth Breakdown at Age 60</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={[calculations.timelineData.find(d => d.age === 60) || {}]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="age" />
                  <YAxis tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`} />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="super" fill="#8B5CF6" name="Super" />
                  <Bar dataKey="property" fill="#3B82F6" name="Property" />
                  <Bar dataKey="emergency" fill="#10B981" name="Emergency Fund" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialPlanner;